/* Symbol + label overlay with declutter.
 *
 * Replaces one MapLibre Marker per entity. Markers cannot cooperate — each one
 * positions itself independently, so close units overlap and their labels become an
 * illegible pile (the VIPER 11 / VIPER 12 defect, and every unit collapsing into one
 * clump at globe zoom).
 *
 * Instead: one absolutely-positioned overlay, projected from the map each frame, with
 * screen-space collision resolution. That also puts us one step from deck.gl, which is
 * what NFR-PERF-06 (5,000 symbols at 60fps) will need.
 *
 * Declutter rules, in priority order:
 *   1. Symbols always render. Hiding a unit is never acceptable.
 *   2. Labels are dropped when they would collide with an already-placed label.
 *   3. Priority: selected > own units > contacts by descending confidence. The
 *      operator's own force is what they are responsible for.
 *   4. Below LABEL_MIN_ZOOM labels are suppressed entirely — at global zoom they
 *      cannot be positioned meaningfully, and a wall of text over a globe is worse
 *      than no text.
 */
import { useEffect, useRef, useState } from 'react'
import type { Map as MlMap } from 'maplibre-gl'
import { useDeck } from '../state/store'
import { stalenessOpacity, symbolSvg } from '../lib/symbols'
import type { OrbisContact, OrbisEntity } from '../data/types'

/** Below this zoom, labels are suppressed and symbols shrink. */
const LABEL_MIN_ZOOM = 4.2
const SYMBOL_MIN_ZOOM = 2.6

interface Placed {
  id: string
  kind: 'entity' | 'contact'
  x: number
  y: number
  size: number
  svg: string
  label: string
  sub: string
  opacity: number
  selected: boolean
  showLabel: boolean
}

interface Box {
  x1: number
  y1: number
  x2: number
  y2: number
}

const overlaps = (a: Box, b: Box) => !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2)

export function SymbolOverlay({ map }: { map: MlMap | null }) {
  const entities = useDeck((s) => s.entities)
  const contacts = useDeck((s) => s.contacts)
  const layers = useDeck((s) => s.layers)
  const selection = useDeck((s) => s.selection)
  const select = useDeck((s) => s.select)
  const standard = useDeck((s) => s.symbolStandard)

  const [placed, setPlaced] = useState<Placed[]>([])
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (!map) return

    const compute = () => {
      const zoom = map.getZoom()
      const showLabels = zoom >= LABEL_MIN_ZOOM
      // Symbols shrink at low zoom so a globe view is not a mass of overlapping frames.
      const size = zoom < SYMBOL_MIN_ZOOM ? 15 : zoom < LABEL_MIN_ZOOM ? 20 : 26

      interface Cand {
        id: string
        kind: 'entity' | 'contact'
        lon: number
        lat: number
        sidc: string
        label: string
        sub: string
        opacity: number
        priority: number
      }
      const cands: Cand[] = []

      if (layers.units) {
        for (const e of entities as OrbisEntity[]) {
          cands.push({
            id: e.id,
            kind: 'entity',
            lon: e.lon,
            lat: e.lat,
            sidc: e.sidc,
            label: e.designation,
            sub:
              e.domain === 'air'
                ? `${e.altitudeFt?.toLocaleString() ?? '—'} ft · ${e.speedKt} kt`
                : e.domain === 'surface'
                  ? `${e.speedKt} kt`
                  : e.className,
            opacity: 1,
            priority: 1000,
          })
        }
      }
      if (layers.contacts) {
        for (const c of contacts as OrbisContact[]) {
          cands.push({
            id: c.id,
            kind: 'contact',
            lon: c.lon,
            lat: c.lat,
            sidc: c.sidc,
            label: c.track,
            sub: `${c.confidence}%`,
            opacity: stalenessOpacity(c.ageSec),
            priority: c.confidence,
          })
        }
      }

      // Selected always wins, then own units, then contacts by confidence.
      cands.sort((a, b) => {
        const aSel = selection?.id === a.id ? 1 : 0
        const bSel = selection?.id === b.id ? 1 : 0
        if (aSel !== bSel) return bSel - aSel
        return b.priority - a.priority
      })

      const taken: Box[] = []
      const out: Placed[] = []

      for (const c of cands) {
        const p = map.project([c.lon, c.lat])
        // Cull anything projected outside the viewport (globe backface included:
        // MapLibre returns coordinates far off-screen for occluded points).
        const { width, height } = map.getCanvas()
        const dpr = window.devicePixelRatio || 1
        if (p.x < -80 || p.y < -80 || p.x > width / dpr + 80 || p.y > height / dpr + 80) continue

        const selected = selection?.id === c.id
        let showLabel = showLabels

        if (showLabel) {
          // Label box sits under the symbol. Width is an estimate from character count
          // — good enough for collision, and far cheaper than measuring text.
          const w = Math.max(c.label.length, c.sub.length) * 5.6 + 8
          const box: Box = {
            x1: p.x - w / 2,
            y1: p.y + size / 2,
            x2: p.x + w / 2,
            y2: p.y + size / 2 + 20,
          }
          if (taken.some((t) => overlaps(box, t))) showLabel = false
          else taken.push(box)
        }

        out.push({
          id: c.id,
          kind: c.kind,
          x: p.x,
          y: p.y,
          size,
          svg: symbolSvg(c.sidc, {
            size,
            contact: c.kind === 'contact',
            selected,
            opacity: c.opacity,
            standard,
          }),
          label: c.label,
          sub: c.sub,
          opacity: c.opacity,
          selected,
          showLabel,
        })
      }

      setPlaced(out)
    }

    const schedule = () => {
      if (frameRef.current !== null) return
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        compute()
      })
    }

    compute()
    map.on('move', schedule)
    map.on('zoom', schedule)
    map.on('resize', schedule)
    return () => {
      map.off('move', schedule)
      map.off('zoom', schedule)
      map.off('resize', schedule)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [map, entities, contacts, layers.units, layers.contacts, selection, standard])

  if (!map) return null

  return (
    // pointer-events-none on the container, re-enabled per symbol, so the map keeps
    // its own pan/zoom handling everywhere else.
    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 10 }}>
      {placed.map((s) => (
        <div
          key={s.id}
          className="absolute flex flex-col items-center"
          style={{ left: s.x, top: s.y, transform: 'translate(-50%, -50%)' }}
        >
          <button
            className="pointer-events-auto block cursor-pointer border-0 bg-transparent p-0"
            onClick={(e) => {
              e.stopPropagation()
              select({ kind: s.kind, id: s.id })
            }}
            aria-label={`${s.label} — ${s.sub}`}
            title={`${s.label} · ${s.sub}`}
            dangerouslySetInnerHTML={{ __html: s.svg }}
          />
          {s.showLabel && (
            <div
              className="pointer-events-none mt-0.5 whitespace-nowrap rounded-sm px-1 py-px text-center leading-tight"
              style={{
                opacity: s.opacity,
                background: s.selected ? 'var(--color-accent-dim)' : 'rgba(7,13,24,0.82)',
              }}
            >
              <div
                className="num"
                style={{ fontSize: 9.5, letterSpacing: '.03em', color: 'var(--color-text-1)' }}
              >
                {s.label}
              </div>
              <div className="num" style={{ fontSize: 8.5, color: 'var(--color-text-3)' }}>
                {s.sub}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
